import { createContext, useContext, useRef } from "react"
import { UseFormReturn } from "react-hook-form"
import { create, useStore } from "zustand"
import { AnamnesisFormType } from "@/modules/patients/detail/progress-note/components/progress-note-editable/anamnesis-section"
import {
  TreatmentPlanFormInput,
  TreatmentPlanFormType,
} from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { QuestionnaireFormType } from "@/modules/patients/detail/progress-note/components/questionnaire"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { AssignEpisodeOfCareFormType } from "@/modules/patients/detail/progress-note/progress-note-card-header"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"

type ProgressNoteFormInput =
  | {
      kind: "TreatmentPlan"
      data: UseFormReturn<TreatmentPlanFormInput, unknown, TreatmentPlanFormType>
    }
  | {
      kind: "Anamnesis"
      data: UseFormReturn<AnamnesisFormType>
    }
  | {
      kind: "AssignEpisodeOfCare"
      data: UseFormReturn<AssignEpisodeOfCareFormType>
    }
  | {
      kind: "PointOfCare"
      data: UseFormReturn<PointOfCareFormType>
    }
  | {
      kind: "QuestionSummary"
      data: UseFormReturn<QuestionnaireFormType>
    }
  | {
      kind: "BetterDocMSNReferralLetter"
      data: {
        hasErrors: boolean
      }
    }
  | {
      kind: "DeBetterDocReferralLetter"
      data: {
        hasErrors: boolean
      }
    }

export type ProgressNoteForm = {
  id: string
  episodeOfCareProgressNoteData: ProgressNoteData
  treatmentPlanForm?: UseFormReturn<TreatmentPlanFormInput, unknown, TreatmentPlanFormType>
  anamnesisForm?: UseFormReturn<AnamnesisFormType>
  assignEpisodeOfCareForm?: UseFormReturn<AssignEpisodeOfCareFormType>
  pointOfCareForm?: UseFormReturn<PointOfCareFormType>
  questionnaireForm?: UseFormReturn<QuestionnaireFormType>
  betterDocMSNReferralLetterForm?: {
    hasErrors: boolean
  }
  deBetterDocReferralLetterForm?: {
    hasErrors: boolean
  }
}
interface ProgressNoteFormState {
  progressNoteForms: Map<string, ProgressNoteForm>
  setProgressNoteForm: (
    id: string,
    episodeOfCareProgressNoteData: ProgressNoteData,
    formInput: ProgressNoteFormInput,
  ) => void
  getProgressNoteTreatmentPlanForm: (
    id: string,
  ) => UseFormReturn<TreatmentPlanFormInput, unknown, TreatmentPlanFormType> | undefined
  clearBetterDocReferralLetterErrors: (id: string) => void
  deleteProgressNoteForm: (id: string) => void
  deleteProgressNoteFormByAppointmentId: (appointmentId: string) => void
  cleanProgressNoteForms: () => void
}

const createProgressNoteFormStore = () => {
  return create<ProgressNoteFormState>((set, get) => ({
    progressNoteForms: new Map<string, ProgressNoteForm>(),
    setProgressNoteForm: (
      id: string,
      episodeOfCareProgressNoteData: ProgressNoteData,
      formInput: ProgressNoteFormInput,
    ) => {
      let progressNoteForm = get().progressNoteForms.get(id)

      if (progressNoteForm === undefined) {
        progressNoteForm = {
          id,
          episodeOfCareProgressNoteData,
        }
      }

      switch (formInput.kind) {
        case "TreatmentPlan":
          progressNoteForm.treatmentPlanForm = formInput.data
          break
        case "Anamnesis":
          progressNoteForm.anamnesisForm = formInput.data
          break
        case "AssignEpisodeOfCare":
          progressNoteForm.assignEpisodeOfCareForm = formInput.data
          break
        case "PointOfCare":
          progressNoteForm.pointOfCareForm = formInput.data
          break
        case "QuestionSummary":
          progressNoteForm.questionnaireForm = formInput.data
          break
        case "BetterDocMSNReferralLetter": {
          progressNoteForm.betterDocMSNReferralLetterForm = {
            hasErrors: formInput.data.hasErrors,
          }
          break
        }
        case "DeBetterDocReferralLetter":
          progressNoteForm.deBetterDocReferralLetterForm = {
            hasErrors: formInput.data.hasErrors,
          }
          break
      }

      progressNoteForm.episodeOfCareProgressNoteData = episodeOfCareProgressNoteData
      const newProgressNoteForms = new Map(get().progressNoteForms)
      newProgressNoteForms.set(id, progressNoteForm)

      set({ progressNoteForms: newProgressNoteForms })
    },
    getProgressNoteTreatmentPlanForm: (id: string) => {
      return get().progressNoteForms.get(id)?.treatmentPlanForm
    },
    clearBetterDocReferralLetterErrors: (id: string) => {
      const progressNoteForm = get().progressNoteForms.get(id)
      if (!progressNoteForm) return

      get().setProgressNoteForm(id, progressNoteForm.episodeOfCareProgressNoteData, {
        kind: "BetterDocMSNReferralLetter",
        data: { hasErrors: false },
      })
    },
    deleteProgressNoteForm: (id: string) => {
      const newProgressNoteForms = new Map(get().progressNoteForms)
      newProgressNoteForms.delete(id)

      set({ progressNoteForms: newProgressNoteForms })
    },
    deleteProgressNoteFormByAppointmentId: (appointmentId: string) => {
      const newProgressNoteForms = new Map(get().progressNoteForms)

      for (const [key, value] of newProgressNoteForms) {
        if (value.episodeOfCareProgressNoteData.appointment.id === appointmentId) {
          newProgressNoteForms.delete(key)
        }
      }

      set({ progressNoteForms: newProgressNoteForms })
    },
    cleanProgressNoteForms: () => {
      set({ progressNoteForms: new Map<string, ProgressNoteForm>() })
    },
  }))
}

type ProgressNoteFormStoreType = ReturnType<typeof createProgressNoteFormStore>

export const ProgressNoteFormContext = createContext<ProgressNoteFormStoreType | null>(null)

type ProgressNoteFormProviderProps = React.PropsWithChildren

export const ProgressNoteFormProvider = ({ children }: ProgressNoteFormProviderProps) => {
  const storeRef = useRef<ProgressNoteFormStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createProgressNoteFormStore()
  }
  return <ProgressNoteFormContext.Provider value={storeRef.current}>{children}</ProgressNoteFormContext.Provider>
}

export function useProgressNoteFormContext<T>(selector: (state: ProgressNoteFormState) => T): T {
  const store = useContext(ProgressNoteFormContext)
  if (!store) throw new Error("Missing ProgressNoteFormProvider in the tree")
  return useStore(store, selector)
}
